import { IApiPaginationData, IApiStandardResponse } from './api/api.models';
import { IndirizzoSpedizione } from './indirizzo.model';
import { IConsensi } from './privacy.model';

/**
 * Interfaccia che definisce la struttura dei dati dell'utente.
 * 
 * Contiene tutte le informazioni relative all'utente del sistema, inclusi
 * dati anagrafici, contatti, informazioni di spedizione e consensi privacy.
 */
export interface IUser {
  /** Identificativo univoco dell'utente */
  id: number;
  /** Identificativo dello sponsor che ha invitato l'utente */
  id_sponsor: number;
  /** Identificativo dell'anagrafica associata */
  id_anagrafica: number;
  
  /** Nome dell'utente */
  nome: string;
  /** Cognome dell'utente */
  cognome: string;
  /** Codice fiscale dell'utente */
  codice_fiscale: string;
 
  /** Indirizzo email dell'utente */
  email: string;
  /** Flag che indica se l'email è stata verificata */
  email_is_verified: boolean;
  /** Data e ora di verifica dell'email */
  email_verified_at: string;
 
  /** Numero di cellulare dell'utente */
  cellulare: string;
  /** Flag che indica se il cellulare è stato verificato */
  cellulare_is_verified: boolean;
  /** Data e ora di verifica del cellulare */
  cellulare_verified_at: string;
 
  /** Data di creazione dell'utente */
  created_at: string;
  /** Data di ultimo aggiornamento dell'utente */
  updated_at: string;
  /** Ruoli assegnati all'utente */
  roles: string;
  /** Target di appartenenza dell'utente */
  target: ProfiloRuolo | null;
  /** Flag che indica se l'utente è bloccato */
  bloccato: boolean;
 
  /** Nominativo per la spedizione */
  spedizione_nominativo: string;
  /** Indirizzo di spedizione */
  spedizione_indirizzo: string;
  /** CAP per la spedizione */
  spedizione_cap: string;
  /** Comune di spedizione */
  spedizione_comune: string;
  /** Provincia di spedizione */
  spedizione_provincia: string;
  /** Identificativo del comune di spedizione */
  spedizione_id_comune: number;
 
  /** Consenso per il trattamento dei dati personali */
  privacy_trattamento_dati: boolean;
  /** Consenso per il trattamento dei dati sensibili */
  privacy_dati_sensibili: boolean;
  /** Consenso per attività commerciali */
  privacy_attivita_commerciali: boolean;
  /** Consenso per attività per conto terzi */
  privacy_attivita_conto_terzi: boolean;
  /** Data di ultimo aggiornamento dei consensi privacy */
  privacy_updated_at: string;
 
  /** Array dei profili associati all'utente */
  profili: IProfilo[] | null;
}

/**
 * Interfaccia per la risposta API contenente i dati dell'utente.
 * 
 * Estende la risposta API standard per includere eventuali campi specifici
 * per le operazioni sugli utenti.
 */
export interface IUserApiResponse extends IApiStandardResponse<IUser> {
  // Qui possiamo aggiungere eventuali campi specifici per la risposta utente, se necessario
}

/**
 * Classe che implementa l'interfaccia IUser e fornisce metodi di utilità.
 * 
 * Rappresenta un utente del sistema con tutti i suoi dati e fornisce
 * metodi per verificare lo stato dell'utente e manipolare i suoi dati.
 */
export class User implements IUser {
  id: number;
  nome: string;
  cognome: string;
  email: string;
  email_is_verified: boolean;
  cellulare: string;
  cellulare_is_verified: boolean;
  codice_fiscale: string;
  created_at: string;
  updated_at: string;
  roles: string;
  target: ProfiloRuolo | null;
  id_sponsor: number;
  id_anagrafica: number;
  bloccato: boolean;
  spedizione_nominativo: string;
  spedizione_indirizzo: string;
  spedizione_cap: string;
  spedizione_comune: string;
  spedizione_provincia: string;
  email_verified_at: string;
  cellulare_verified_at: string;
  privacy_trattamento_dati: boolean;
  privacy_dati_sensibili: boolean;
  privacy_attivita_commerciali: boolean;
  privacy_attivita_conto_terzi: boolean;
  privacy_updated_at: string;
  spedizione_id_comune: number;
  profili: IProfilo[] | null;

  /**
   * Costruttore della classe User.
   * 
   * @param user I dati dell'utente da utilizzare per inizializzare l'istanza
   */
  constructor(user: IUser) {
    this.id = user.id;
    this.nome = user.nome;
    this.cognome = user.cognome;
    this.email = user.email;
    this.email_is_verified = user.email_is_verified;
    this.cellulare = user.cellulare;
    this.cellulare_is_verified = user.cellulare_is_verified;
    this.codice_fiscale = user.codice_fiscale;
    this.created_at = user.created_at;
    this.updated_at = user.updated_at;
    this.roles = user.roles;
    this.target = user.target;
    this.id_sponsor = user.id_sponsor;
    this.id_anagrafica = user.id_anagrafica;
    this.bloccato = user.bloccato;
    this.spedizione_nominativo = user.spedizione_nominativo;
    this.spedizione_indirizzo = user.spedizione_indirizzo;
    this.spedizione_cap = user.spedizione_cap;
    this.spedizione_comune = user.spedizione_comune;
    this.spedizione_provincia = user.spedizione_provincia;
    this.email_verified_at = user.email_verified_at;
    this.cellulare_verified_at = user.cellulare_verified_at;
    this.privacy_trattamento_dati = user.privacy_trattamento_dati;
    this.privacy_dati_sensibili = user.privacy_dati_sensibili;
    this.privacy_attivita_commerciali = user.privacy_attivita_commerciali;
    this.privacy_attivita_conto_terzi = user.privacy_attivita_conto_terzi;
    this.privacy_updated_at = user.privacy_updated_at;
    this.spedizione_id_comune = user.spedizione_id_comune;
    this.profili = user.profili;
  }

  /**
   * Verifica se l'utente è completamente verificato.
   * 
   * Un utente è considerato verificato se ha un ID valido e sia l'email
   * che il cellulare sono stati verificati.
   * 
   * @returns True se l'utente ha ID valido, email e cellulare verificati
   */
  isVerified(): boolean {
    return this.id > 0 && 
           this.cellulare_is_verified === true && 
           this.email_is_verified === true;
  }

  /**
   * Verifica se l'utente è autenticato.
   * 
   * @returns True se l'utente ha un ID valido (maggiore di 0)
   */
  isAuthenticated(): boolean {
    return this.id > 0;
  }

  /**
   * Verifica se i contatti dell'utente sono verificati.
   * 
   * @returns True se sia cellulare che email sono verificati
   */
  isContattiVerificati(): boolean {
    return this.cellulare_is_verified === true && 
           this.email_is_verified === true;
  }

  /**
   * Verifica se la registrazione dell'utente è completata.
   * 
   * Al momento equivale alla verifica dei contatti.
   * 
   * @returns True se la registrazione è completata
   */
  isRegistrazioneCompletata(): boolean {
    return this.isContattiVerificati();
  }

  /**
   * Restituisce il nome completo dell'utente.
   * 
   * @returns Il nome e cognome concatenati, con spazi extra rimossi
   */
  getFullName(): string {
    return `${this.nome} ${this.cognome}`.trim();
  }

  /**
   * Verifica se l'utente ha fornito tutti i consensi privacy richiesti.
   * 
   * @returns True se tutti i consensi privacy sono stati accordati
   */
  hasPrivacyConsents(): boolean {
    return this.privacy_trattamento_dati && 
           this.privacy_dati_sensibili && 
           this.privacy_attivita_commerciali && 
           this.privacy_attivita_conto_terzi;
  }

  /**
   * Aggiorna i consensi privacy dell'utente.
   * 
   * @param consensi I nuovi consensi privacy da impostare
   */
  aggiornaUserConsensiPrivacy(consensi: IConsensi): void {
    this.privacy_trattamento_dati = consensi.privacy_trattamento_dati;
    this.privacy_dati_sensibili = consensi.privacy_dati_sensibili;
    this.privacy_attivita_commerciali = consensi.privacy_attivita_commerciali;
    this.privacy_attivita_conto_terzi = consensi.privacy_attivita_conto_terzi;
  }

  /**
   * Aggiorna i consensi privacy dell'anagrafica.
   * 
   * @param consensi I nuovi consensi privacy da impostare per l'anagrafica
   */
  aggiornaAnagraficaConsensiPrivacy(consensi: IConsensi): void {
    this.privacy_trattamento_dati = consensi.privacy_trattamento_dati;
    this.privacy_dati_sensibili = consensi.privacy_dati_sensibili;
    this.privacy_attivita_commerciali = consensi.privacy_attivita_commerciali;
    this.privacy_attivita_conto_terzi = consensi.privacy_attivita_conto_terzi;
  }

  /**
   * Aggiorna l'indirizzo di spedizione dell'utente.
   * 
   * @param indirizzo Il nuovo indirizzo di spedizione da impostare
   */
  aggiornaIndirizzoSpedizione(indirizzo: IndirizzoSpedizione): void {
    this.spedizione_nominativo = indirizzo.nominativo;
    this.spedizione_cap = indirizzo.cap;
    this.spedizione_id_comune = indirizzo.id_comune;
    this.spedizione_indirizzo = indirizzo.indirizzo;
    this.spedizione_comune = indirizzo.comune.descrizione;
    this.spedizione_provincia = indirizzo.comune.provincia;
  }

  /**
   * Converte l'istanza User in un oggetto IUser.
   * 
   * @returns L'oggetto IUser corrispondente a questa istanza
   */
  toJSON(): IUser {
    return {
      id: this.id,
      nome: this.nome,
      cognome: this.cognome,
      email: this.email,
      email_is_verified: this.email_is_verified,
      cellulare: this.cellulare,
      cellulare_is_verified: this.cellulare_is_verified,
      codice_fiscale: this.codice_fiscale,
      created_at: this.created_at,
      updated_at: this.updated_at,
      roles: this.roles,
      target: this.target,
      id_sponsor: this.id_sponsor,
      id_anagrafica: this.id_anagrafica,
      bloccato: this.bloccato,
      spedizione_nominativo: this.spedizione_nominativo,
      spedizione_indirizzo: this.spedizione_indirizzo,
      spedizione_cap: this.spedizione_cap,
      spedizione_comune: this.spedizione_comune,
      spedizione_provincia: this.spedizione_provincia,
      email_verified_at: this.email_verified_at,
      cellulare_verified_at: this.cellulare_verified_at,
      privacy_trattamento_dati: this.privacy_trattamento_dati,
      privacy_dati_sensibili: this.privacy_dati_sensibili,
      privacy_attivita_commerciali: this.privacy_attivita_commerciali,
      privacy_attivita_conto_terzi: this.privacy_attivita_conto_terzi,
      privacy_updated_at: this.privacy_updated_at,
      spedizione_id_comune: this.spedizione_id_comune,
      profili: this.profili,
    };
  }
}

/**
 * Istanza di User vuota utilizzata come valore di default.
 * 
 * Rappresenta un utente con tutti i campi inizializzati ai valori di default.
 * Utilizzata per inizializzare i servizi quando non è presente un utente autenticato.
 */
export const EMPTY_USER: User = new User({
  id: 0,
  nome: '',
  cognome: '',
  email: '',
  email_is_verified: false,
  cellulare: '',
  cellulare_is_verified: false,
  codice_fiscale: '',
  created_at: '',
  updated_at: '',
  roles: '',
  target: null,
  id_sponsor: 0,
  id_anagrafica: 0,
  bloccato: true,
  spedizione_nominativo: '',
  spedizione_indirizzo: '',
  spedizione_cap: '',
  spedizione_comune: '',
  spedizione_provincia: '',
  email_verified_at: '',
  cellulare_verified_at: '',
  privacy_trattamento_dati: false,
  privacy_dati_sensibili: false,
  privacy_attivita_commerciali: false,
  privacy_attivita_conto_terzi: false,
  privacy_updated_at: '',
  spedizione_id_comune: 0,
  profili: []
});

/**
 * Interfaccia per i titoli di studio.
 * 
 * Definisce la struttura dei dati per i titoli di studio disponibili nel sistema.
 */
export interface ITitoloDiStudio {
  /** Tipo identificativo dell'interfaccia */
  tipo: 'ITitoloDiStudio';
  /** Codice numerico del titolo di studio */
  codice: number;
  /** Descrizione del titolo di studio */
  descrizione: string;
  /** Codice utilizzato dalla banca dati */
  codice_banca: string;
}

/**
 * Interfaccia per la risposta API dei titoli di studio.
 */
export interface IAPITitoloDiStudio {
  tipo: 'ITitoloDiStudio';
  data: ITitoloDiStudio[];
}

/**
 * Classe che implementa l'interfaccia ITitoloDiStudio.
 */
export class ITitoloDiStudio implements ITitoloDiStudio {
  tipo: 'ITitoloDiStudio' = 'ITitoloDiStudio';
  codice: number;
  descrizione: string;
  codice_banca: string;
  
  /**
   * Costruttore per ITitoloDiStudio.
   * 
   * @param codice Codice numerico del titolo di studio
   * @param descrizione Descrizione del titolo di studio
   * @param codice_banca Codice utilizzato dalla banca dati
   */
  constructor(codice = 0, descrizione = '', codice_banca = '') {
    this.codice = codice;
    this.descrizione = descrizione;
    this.codice_banca = codice_banca;
  }
}

/**
 * Interfaccia per le nature giuridiche.
 * 
 * Definisce la struttura dei dati per le nature giuridiche delle persone giuridiche.
 */
export interface INaturaGiuridica {
  /** Identificativo univoco */
  id: number;
  /** Codice BI (Business Intelligence) */
  cod_bi: string;
  /** Descrizione breve della natura giuridica */
  descr_breve: string;
  /** Descrizione completa della natura giuridica */
  descrizione: string;
  /** Indicatore se è di capitale (1) o no (0) */
  di_capitale: number;
}

/**
 * Classe che implementa l'interfaccia INaturaGiuridica.
 */
export class NaturaGiuridica implements INaturaGiuridica {
  id: number;
  cod_bi: string;
  descr_breve: string;
  descrizione: string;
  di_capitale: number;
  
  /**
   * Costruttore per NaturaGiuridica.
   * 
   * @param id Identificativo univoco
   * @param cod_bi Codice BI
   * @param descr_breve Descrizione breve
   * @param descrizione Descrizione completa
   * @param di_capitale Indicatore di capitale
   */
  constructor(
    id = 0,
    cod_bi = '',
    descr_breve = '',
    descrizione = '',
    di_capitale = 0
  ) {
    this.id = id;
    this.cod_bi = cod_bi;
    this.descr_breve = descr_breve;
    this.descrizione = descrizione;
    this.di_capitale = di_capitale;
  }
}

/**
 * Interfaccia per la risposta API delle nature giuridiche.
 */
export interface IAPINaturaGiuridica {
  tipo: 'INaturaGiuridica';
  data: INaturaGiuridica[];
}

/**
 * Interfaccia per i codici ATECO.
 * 
 * Definisce la struttura dei dati per i codici di attività economica.
 */
export interface ICodiceAteco {
  /** Codice ATECO */
  codice_ateco: string;
  /** Sezione di appartenenza */
  sezione: string;
  /** Descrizione dell'attività */
  descrizione: string;
  /** Versione del codice ATECO */
  versione: string;
  /** Data di validità */
  data: string;
  /** Data di revisione */
  rev_data: string;
}

/**
 * Classe che implementa l'interfaccia ICodiceAteco.
 */
export class CodiceAteco implements ICodiceAteco {
  codice_ateco: string;
  sezione: string;
  descrizione: string;
  versione: string;
  data: string;
  rev_data: string;
  
  /**
   * Costruttore per CodiceAteco.
   * 
   * @param codice_ateco Codice ATECO
   * @param sezione Sezione di appartenenza
   * @param versione Versione del codice
   * @param descrizione Descrizione dell'attività
   * @param data Data di validità
   * @param rev_data Data di revisione
   */
  constructor(
    codice_ateco = '',
    sezione = '',
    versione = '',
    descrizione = '',
    data = '',
    rev_data = ''
  ) {
    this.codice_ateco = codice_ateco;
    this.sezione = sezione;
    this.versione = versione;
    this.descrizione = descrizione;
    this.data = data;
    this.rev_data = rev_data;
  }
}

/**
 * Interfaccia per la risposta API dei codici ATECO.
 */
export interface IAPICodiceAteco {
  tipo: 'ICodiceAteco';
  data: ICodiceAteco[];
}

/**
 * Interfaccia per le categorie merceologiche.
 */
/**
 * Interfaccia per le categorie merceologiche.
 */
export interface ICategorieMerceologiche {
  /** Identificativo della categoria merceologica */
  id_categoria_merceologica: number;
  /** Descrizione della categoria */
  descrizione: string;
  /** Operatore che ha effettuato l'ultima modifica */
  operatore: string;
  /** Data dell'ultima modifica */
  data_modifica: string;
  /** Flag di cancellazione (0/1) */
  cancellato: number;
}

/**
 * Classe che implementa l'interfaccia ICategorieMerceologiche.
 */
export class CategorieMerceologiche implements ICategorieMerceologiche {
  id_categoria_merceologica: number;
  descrizione: string;
  operatore: string;
  data_modifica: string;
  cancellato: number;
  
  /**
   * Costruttore per CategorieMerceologiche.
   * 
   * @param id_categoria_merceologica Identificativo della categoria
   * @param descrizione Descrizione della categoria
   * @param operatore Operatore che ha effettuato la modifica
   * @param data_modifica Data dell'ultima modifica
   * @param cancellato Flag di cancellazione (0/1)
   */
  constructor(
    id_categoria_merceologica = 0,
    descrizione = '',
    operatore = '',
    data_modifica = '',
    cancellato = 0
  ) {
    this.id_categoria_merceologica = id_categoria_merceologica;
    this.descrizione = descrizione;
    this.operatore = operatore;
    this.data_modifica = data_modifica;
    this.cancellato = cancellato;
  }
}

/**
 * Interfaccia per la risposta API delle categorie merceologiche.
 */
export interface IAPCategorieMerceologiche extends IApiStandardResponse<ICategorieMerceologiche[]> {}

/**
 * Interfaccia per i tipi di documento di riconoscimento.
 */
export interface ITipoDocumento {
  /** Codice del tipo di documento */
  codice: number;
  /** Descrizione del tipo di documento */
  descrizione: string;
  /** Validità in anni del documento */
  validita: number;
  /** Codice dell'ente che rilascia il documento */
  rilasciato_da: number;
  /** Codice utilizzato dalla banca dati */
  codice_banca: string;
}

/**
 * Classe che implementa l'interfaccia ITipoDocumento.
 */
export class TipoDocumento implements ITipoDocumento {
  codice: number;
  descrizione: string;
  validita: number;
  rilasciato_da: number;
  codice_banca: string;
  
  /**
   * Costruttore per TipoDocumento.
   * 
   * @param codice Codice del tipo di documento
   * @param descrizione Descrizione del tipo di documento
   * @param validita Validità in anni del documento
   * @param rilasciato_da Codice dell'ente che rilascia il documento
   * @param codice_banca Codice utilizzato dalla banca dati
   */
  constructor(
    codice = 0,
    descrizione = '',
    validita = 0,
    rilasciato_da = 0,
    codice_banca = ''
  ) {
    this.codice = codice;
    this.descrizione = descrizione;
    this.validita = validita;
    this.rilasciato_da = rilasciato_da;
    this.codice_banca = codice_banca;
  }
}

/**
 * Interfaccia per la risposta API dei tipi di documento.
 */
export interface IAPITipoDocumento {
  tipo: 'ITipoDocumento';
  data: ITipoDocumento[];
}

/**
 * Interfaccia per gli stati civili.
 */
export interface IStatoCivile {
  /** Codice dello stato civile */
  codice: number;
  /** Codice utilizzato dalla banca dati */
  codice_banca: string;
  /** Descrizione dello stato civile */
  descrizione: string;
}

/**
 * Classe che implementa l'interfaccia IStatoCivile.
 */
export class StatoCivile implements IStatoCivile {
  codice: number;
  codice_banca: string;
  descrizione: string;
  
  /**
   * Costruttore per StatoCivile.
   * 
   * @param codice Codice dello stato civile
   * @param codice_banca Codice utilizzato dalla banca dati
   * @param descrizione Descrizione dello stato civile
   */
  constructor(codice = 0, codice_banca = '', descrizione = '') {
    this.codice = codice;
    this.codice_banca = codice_banca;
    this.descrizione = descrizione;
  }
}

/**
 * Interfaccia per la risposta API degli stati civili.
 */
export interface IAPIStatoCivile {
  tipo: 'IStatoCivile';
  data: IStatoCivile[];
}

/**
 * Tipo che definisce i possibili ruoli di un profilo utente.
 */
export type ProfiloRuolo = 'convenzionato' | 'manifestazione' | 'promotore';

/**
 * Tipo che definisce i ruoli attivi, includendo anche 'indefinito'.
 */
export type RuoloAttivo = ProfiloRuolo | '';

/**
 * Interfaccia per i profili utente.
 * 
 * Definisce la struttura dei dati per i profili associati agli utenti,
 * che determinano i ruoli e le autorizzazioni nel sistema.
 */
export interface IProfilo {
  /** Identificativo del segnalatore */
  id_segnalatore: number;
  /** Nome utente del profilo */
  username: string;
  /** Ruolo del profilo */
  ruolo: ProfiloRuolo;
  /** Flag che indica se il profilo è bloccato */
  bloccato: boolean;
  /** Attributi aggiuntivi del profilo in formato stringa */
  attributi: string;
  /** Identificativo dell'anagrafica associata */
  id_anagrafica: number;
  /** Descrizione dell'anagrafica */
  anagrafica: string;
  /** Identificativo dell'agenzia */
  id_agenzia: number;
  /** Nome dell'agenzia */
  agenzia: string;
  /** Data di creazione del profilo */
  created_at: string;
  /** Data di ultimo aggiornamento del profilo */
  updated_at: string;
}

/**
 * Interfaccia per la risposta API di un singolo profilo.
 */
export interface IApiProfilo extends IApiStandardResponse<IProfilo> {
  message: string;
  data: IProfilo;
}

/**
 * Classe che implementa l'interfaccia IProfilo.
 */
export class Profilo implements IProfilo {
  username: string;
  bloccato: boolean;
  ruolo: ProfiloRuolo;
  id_segnalatore: number;
  id_anagrafica: number;
  id_agenzia: number;
  agenzia: string;
  attributi: string;
  created_at: string;
  updated_at: string;
  anagrafica: string;

  /**
   * Costruttore per Profilo.
   * 
   * @param ruolo Ruolo del profilo
   * @param user_name Nome utente del profilo
   * @param bloccato Flag che indica se il profilo è bloccato
   * @param id_segnalatore Identificativo del segnalatore
   * @param id_anagrafica Identificativo dell'anagrafica associata
   * @param id_agenzia Identificativo dell'agenzia
   * @param agenzia Nome dell'agenzia
   * @param attributi Attributi aggiuntivi del profilo
   * @param created_at Data di creazione del profilo
   * @param updated_at Data di ultimo aggiornamento del profilo
   * @param anagrafica Descrizione dell'anagrafica
   */
  constructor(
    ruolo: ProfiloRuolo,
    username = '',
    bloccato = true,
    id_segnalatore = 0,
    id_anagrafica = 0,
    id_agenzia = 0,
    agenzia = '',
    attributi = '',
    created_at = '',
    updated_at = '',
    anagrafica = ''
  ) {
    this.username = username;
    this.bloccato = bloccato;
    this.ruolo = ruolo;
    this.id_segnalatore = id_segnalatore;
    this.id_anagrafica = id_anagrafica;
    this.id_agenzia = id_agenzia;
    this.agenzia = agenzia;
    this.attributi = attributi;
    this.created_at = created_at;
    this.updated_at = updated_at;
    this.anagrafica = anagrafica;
  }
}

/**
 * Istanza di Profilo vuota utilizzata come valore di default.
 * 
 * Utilizzata per inizializzare i servizi quando non è presente un profilo selezionato.
 */
export const EMPTY_PROFILO: Profilo = new Profilo(
  'manifestazione', // ruolo di default
  '',              // username
  true,              // bloccato
  0,               // id_segnalatore
  0,               // id_anagrafica
  0,               // id_agenzia
  '',              // agenzia
  '',              // attributi
  '',              // created_at
  '',              // updated_at
  ''               // anagrafica
);

/**
 * Interfaccia per le qualifiche professionali.
 */
export interface IQualificaProfessionale {
  /** Codice della qualifica professionale */
  codice: number;
  /** Descrizione della qualifica professionale */
  descrizione: string;
  /** Codice utilizzato dalla banca dati */
  codice_banca: string;
}

/**
 * Classe che implementa l'interfaccia IQualificaProfessionale.
 */
export class QualificaProfessionale implements IQualificaProfessionale {
  codice: number;
  descrizione: string;
  codice_banca: string;
  
  /**
   * Costruttore per QualificaProfessionale.
   * 
   * @param codice Codice della qualifica professionale
   * @param codice_banca Codice utilizzato dalla banca dati
   * @param descrizione Descrizione della qualifica professionale
   */
  constructor(codice = 0, codice_banca = '', descrizione = '') {
    this.codice = codice;
    this.descrizione = descrizione;
    this.codice_banca = codice_banca;
  }
}

/**
 * Interfaccia per la risposta API delle qualifiche professionali.
 */
export interface APIQualificaProfessionale {
  tipo: 'IQualificaProfessionale';
  data: IQualificaProfessionale[];
}

/**
 * Interfaccia per i tipi di assunzione.
 */
export interface IAssunzione {
  /** Codice del tipo di assunzione */
  codice: number;
  /** Codice utilizzato dalla banca dati */
  codice_banca: string;
  /** Descrizione del tipo di assunzione */
  descrizione: string;
}

/**
 * Classe che implementa l'interfaccia IAssunzione.
 */
export class Assunzione implements IAssunzione {
  codice: number;
  codice_banca: string;
  descrizione: string;
  
  /**
   * Costruttore per Assunzione.
   * 
   * @param codice Codice del tipo di assunzione
   * @param codice_banca Codice utilizzato dalla banca dati
   * @param descrizione Descrizione del tipo di assunzione
   */
  constructor(codice = 0, codice_banca = '', descrizione = '') {
    this.codice = codice;
    this.descrizione = descrizione;
    this.codice_banca = codice_banca;
  }
}

/**
 * Interfaccia per la risposta API dei tipi di assunzione.
 */
export interface APIAssunzione {
  tipo: 'IAssunzione';
  data: IAssunzione[];
}

/**
 * Interfaccia per le categorie lavorative.
 */
export interface ICategoriaLavorativa {
  /** Codice della categoria lavorativa */
  codice: number;
  /** Identificativo del comparto */
  id_comparto: number;
  /** Descrizione della categoria lavorativa */
  descrizione: string;
  /** Operatore che ha effettuato l'ultima modifica */
  operatore: string;
  /** Data dell'ultima modifica */
  data_modifica: string;
}

/**
 * Classe che implementa l'interfaccia ICategoriaLavorativa.
 */
export class CategoriaLavorativa implements ICategoriaLavorativa {
  codice: number;
  id_comparto: number;
  descrizione: string;
  operatore: string;
  data_modifica: string;
  
  /**
   * Costruttore per CategoriaLavorativa.
   * 
   * @param codice Codice della categoria lavorativa
   * @param id_comparto Identificativo del comparto
   * @param descrizione Descrizione della categoria lavorativa
   * @param operatore Operatore che ha effettuato l'ultima modifica
   * @param data_modifica Data dell'ultima modifica
   */
  constructor(
    codice = 0,
    id_comparto = 0,
    descrizione = '',
    operatore = '',
    data_modifica = ''
  ) {
    this.codice = codice;
    this.id_comparto = id_comparto;
    this.descrizione = descrizione;
    this.operatore = operatore;
    this.data_modifica = data_modifica;
  }
}

/**
 * Interfaccia per la risposta API delle categorie lavorative.
 */
export interface APICategoriaLavorativa {
  tipo: 'ICategoriaLavorativa';
  data: ICategoriaLavorativa[];
}

/**
 * Interfaccia per le pratiche.
 * 
 * Definisce la struttura dei dati per le pratiche gestite nel sistema.
 */
export interface IPratica {
  /** Identificativo della scheda associata */
  id_scheda: number;
  /** Data di registrazione della pratica */
  data_registrazione: string;
  /** Identificativo dello stato della pratica */
  id_stato: number;
  /** Descrizione dello stato della pratica */
  stato: string;
  /** Numero di sequenza della pratica */
  sequenza: number;
  /** Nominativo associato alla pratica */
  nominativo: string;
  /** Identificativo del tipo di finanziamento */
  id_tipo_finanziamento: number;
  /** Descrizione del tipo di finanziamento */
  tipo_finanziamento: string;
  /** Percentuale di completamento della pratica */
  perc_completamento: string;
  /** Flag che indica se la pratica è stata eseguita con successo */
  esito_positivo: boolean;
  /** Flag che indica se la pratica non e' stata chiusa */
  esito_negativo: boolean;
}

/**
 * Interfaccia per la risposta API dell'elenco delle pratiche.
 * 
 * Include i dati di paginazione per la gestione di liste lunghe.
 */
export interface IApiElencoPratiche extends IApiStandardResponse<IPratica[]> {
  /** Informazioni di paginazione */
  pagination: IApiPaginationData;
}

/**
 * Interfaccia per le pratiche del promotore.
 * 
 * Definisce la struttura dei dati per le pratiche gestite dal promotore.
 */
export interface IPraticaPromotore {
  /** Identificativo della scheda associata */
  id_scheda: number;
  /** Data di registrazione della pratica (API: "dd-mm-yyyy"; mock: array) */
  data_registrazione: string | string[];
  /** Identificativo dello stato della pratica */
  id_stato: number;
  /** Descrizione dello stato della pratica */
  stato: string;
  /** Numero di sequenza della pratica */
  sequenza: number;
  /** Nominativo associato alla pratica */
  nominativo: string;
  /** Identificativo del tipo di finanziamento */
  id_tipo_finanziamento: number;
  /** Descrizione del tipo di finanziamento */
  tipo_finanziamento: string;
  /** Percentuale di completamento della pratica */
  perc_completamento: number;
  /** Flag che indica se la pratica è stata eseguita con successo */
  esito_positivo: boolean;
  /** Flag che indica se la pratica non e' stata chiusa */
  esito_negativo: boolean;
}

/**
 * Interfaccia per i segnalatori del promotore.
 * 
 * Definisce la struttura dei dati per i segnalatori associati al promotore.
 */
export interface ISegnalatorePromotore {
  /** Identificativo del segnalatore */
  id_segnalatore: number;
  /** Denominazione del segnalatore */
  denominazione: string;
  /** Identificativo del piano marketing */
  id_piano_marketing: number;
  /** Descrizione del piano marketing */
  piano_marketing: string;
  /** Identificativo della qualifica */
  id_qualifica: number;
  /** Descrizione della qualifica */
  qualifica: string;
  /** Identificativo dello sponsor */
  id_sponsor: number;
  /** Nome dello sponsor */
  sponsor: string;
  /** Livello gerarchico del segnalatore */
  livello: number;
  /** Array delle pratiche associate al segnalatore */
  pratiche: IPraticaPromotore[];
  /** Array dei segnalatori figli (per struttura gerarchica) */
  children?: ISegnalatorePromotore[];
}

/**
 * Interfaccia per la risposta API delle pratiche del promotore.
 * 
 * Include i dati di paginazione per la gestione di liste lunghe.
 */
export interface IApiPromotorePratiche extends IApiStandardResponse<ISegnalatorePromotore[]> {
  /** Informazioni di paginazione */
  pagination: IApiPaginationData;
}

/**
 * Interfaccia per le pratiche del promotore.
 * 
 * Definisce la struttura dei dati per le pratiche gestite dal promotore.
 */
export interface IPraticaConvenzionato {
  /** Identificativo della scheda associata */
  id_scheda: number;
  /** Data di registrazione della pratica (API: "dd-mm-yyyy"; mock: array) */
  data_registrazione: string | string[];
  /** Identificativo dello stato della pratica */
  id_stato: number;
  /** Descrizione dello stato della pratica */
  stato: string;
  /** Numero di sequenza della pratica */
  sequenza: number;
  /** Nominativo associato alla pratica */
  nominativo: string;
  /** Identificativo del tipo di finanziamento */
  id_tipo_finanziamento: number;
  /** Descrizione del tipo di finanziamento */
  tipo_finanziamento: string;
  /** Percentuale di completamento della pratica */
  perc_completamento: number;
  /** Flag che indica se la pratica è stata eseguita con successo */
  esito_positivo: boolean;
  /** Flag che indica se la pratica non e' stata chiusa */
  esito_negativo: boolean;
}

/**
 * Interfaccia per i segnalatori del promotore.
 * 
 * Definisce la struttura dei dati per i segnalatori associati al promotore.
 */

/**
 * Interfaccia per la risposta API delle pratiche del promotore.
 * 
 * Include i dati di paginazione per la gestione di liste lunghe.
 */
export interface IApiConvenzionatoPratiche extends IApiStandardResponse<IPraticaConvenzionato[]> {
  /** Informazioni di paginazione */
  pagination: IApiPaginationData;
}